import React, { useState, useMemo } from 'react';
import { adresaSablony } from '../../utils/adresaUdalosti';
import {
    Box,
    Paper,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Chip,
    Alert,
    Tooltip,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Card,
    CardActionArea,
    CardContent,
    Menu,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Description as DescriptionIcon,
    MoreVert as MoreVertIcon,
    LocationOn as LocationIcon,
    VisibilityOff as VisibilityOffIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { PageHeader, EmptyState, ViewToggle } from '../../components/admin';
import type { ViewMode } from '../../components/admin';
import { useAuth } from '../../context/AuthContext';
import {
    useAllSablonyQuery,
    useDeleteSablonaMutation,
    useToggleSablonaStatusMutation,
    useTypyUdalostiQuery,
    useAllPodtypyQuery,
} from '../../api/JsdhNotifikaceService/service/AdminMutations';
import type {
    SablonaUdalosti,
    TypUdalosti,
    PodtypUdalosti,
} from '../../api/JsdhNotifikaceService/generated';
import { getTemplateIcon } from '../../utils/templateHelpers';
import SablonaWizardDialog from '../../components/admin/SablonaWizardDialog';

const SablonyUdalostiPage: React.FC = () => {
    const { isAdmin } = useAuth();
    const { data: sablony = [], isLoading } = useAllSablonyQuery();
    const { data: typy = [] } = useTypyUdalostiQuery();
    const { data: podtypy = [] } = useAllPodtypyQuery();
    const deleteMutation = useDeleteSablonaMutation();
    const toggleStatusMutation = useToggleSablonaStatusMutation();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [viewMode, setViewMode] = useState<ViewMode>(isMobile ? 'cards' : 'table');
    const [cardMenuAnchor, setCardMenuAnchor] = useState<null | HTMLElement>(null);
    const [cardMenuSablon, setCardMenuSablon] = useState<SablonaUdalosti | null>(null);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingSablona, setEditingSablona] = useState<SablonaUdalosti | null>(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

    const typyMap = useMemo(() => {
        const map = new Map<number, TypUdalosti>();
        typy.forEach((t) => map.set(t.id, t));
        return map;
    }, [typy]);

    const podtypyMap = useMemo(() => {
        const map = new Map<number, PodtypUdalosti>();
        podtypy.forEach((p) => map.set(p.id, p));
        return map;
    }, [podtypy]);

    const nextPoradi = sablony.length > 0
        ? Math.max(...sablony.map(s => s.poradi)) + 1
        : 1;

    const handleOpenCreate = () => {
        setEditingSablona(null);
        setDialogOpen(true);
    };

    const handleOpenEdit = (sablona: SablonaUdalosti) => {
        setEditingSablona(sablona);
        setDialogOpen(true);
    };

    const handleDelete = () => {
        if (deleteConfirmId) {
            deleteMutation.mutate(deleteConfirmId, {
                onSuccess: () => setDeleteConfirmId(null),
            });
        }
    };

    const handleToggleStatus = (sablona: SablonaUdalosti) => {
        toggleStatusMutation.mutate({ id: sablona.id, aktivni: !sablona.aktivni });
    };

    const getTypLabel = (s: SablonaUdalosti) => {
        const typ = s.typId ? typyMap.get(s.typId) : null;
        const podtyp = s.podtypId ? podtypyMap.get(s.podtypId) : null;
        if (podtyp && typ) return `${typ.nazev} / ${podtyp.nazev}`;
        if (typ) return typ.nazev;
        return s.udalost || '-';
    };

    if (!isAdmin) {
        return (
            <Alert severity="info">
                Správa šablon je dostupná pouze pro administrátory.
            </Alert>
        );
    }

    return (
        <Box>
            <PageHeader
                title="Šablony událostí"
                action={
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <ViewToggle value={viewMode} onChange={setViewMode} />
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleOpenCreate}
                        >
                            Nová šablona
                        </Button>
                    </Box>
                }
            />

            {viewMode === 'cards' ? (
                <>
                    {sablony.length === 0 && !isLoading && (
                        <EmptyState
                            icon={<DescriptionIcon />}
                            title="Žádné šablony"
                            description="Vytvořte první šablonu pro rychlé vyhlášení poplachu."
                        />
                    )}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
                        {[...sablony].sort((a, b) => a.poradi - b.poradi).map((s) => {
                            const adresa = adresaSablony(s);
                            return (
                            <Card
                                key={s.id}
                                sx={{
                                    bgcolor: s.barva || '#78909c',
                                    color: '#fff',
                                    position: 'relative',
                                    transition: 'transform 0.15s',
                                    '&:hover': { transform: 'translateY(-2px)' },
                                }}
                            >
                                <IconButton
                                    onClick={(e) => { e.stopPropagation(); setCardMenuAnchor(e.currentTarget); setCardMenuSablon(s); }}
                                    sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1 }}
                                >
                                    <MoreVertIcon sx={{ color: 'rgba(255,255,255,0.8)' }} />
                                </IconButton>
                                <CardActionArea onClick={() => handleOpenEdit(s)}>
                                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                                        <Box sx={{ mb: 1, opacity: 0.9 }}>
                                            {getTemplateIcon(s.ikona)}
                                        </Box>
                                        <Typography variant="h6" fontWeight={700} sx={{ color: '#fff' }}>
                                            {s.nazev}
                                        </Typography>
                                        {adresa && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 1, opacity: 0.8 }}>
                                                <LocationIcon sx={{ fontSize: 16 }} />
                                                <Typography variant="body2">{adresa}</Typography>
                                            </Box>
                                        )}
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', mt: 1.5 }}>
                                            {s.predpoplach && <Chip label="PP" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff' }} />}
                                            <Chip
                                                label={s.aktivni ? 'Aktivní' : 'Neaktivní'}
                                                size="small"
                                                sx={{ bgcolor: s.aktivni ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.3)', color: '#fff' }}
                                            />
                                        </Box>
                                        <Typography variant="caption" sx={{ position: 'absolute', top: 8, left: 12, opacity: 0.5 }}>
                                            #{s.poradi}
                                        </Typography>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                            );
                        })}
                    </Box>
                    <Menu
                        anchorEl={cardMenuAnchor}
                        open={Boolean(cardMenuAnchor)}
                        onClose={() => { setCardMenuAnchor(null); setCardMenuSablon(null); }}
                    >
                        <MenuItem onClick={() => { if (cardMenuSablon) handleOpenEdit(cardMenuSablon); setCardMenuAnchor(null); setCardMenuSablon(null); }}>
                            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                            <ListItemText>Upravit</ListItemText>
                        </MenuItem>
                        <MenuItem
                            onClick={() => { if (cardMenuSablon) handleToggleStatus(cardMenuSablon); setCardMenuAnchor(null); setCardMenuSablon(null); }}
                        >
                            <ListItemIcon>
                                {cardMenuSablon?.aktivni
                                    ? <VisibilityOffIcon fontSize="small" />
                                    : <CheckCircleIcon fontSize="small" color="success" />}
                            </ListItemIcon>
                            <ListItemText>{cardMenuSablon?.aktivni ? 'Deaktivovat' : 'Aktivovat'}</ListItemText>
                        </MenuItem>
                        <Divider />
                        <MenuItem
                            onClick={() => { if (cardMenuSablon) setDeleteConfirmId(cardMenuSablon.id); setCardMenuAnchor(null); setCardMenuSablon(null); }}
                            sx={{ color: 'error.main' }}
                        >
                            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                            <ListItemText>Smazat</ListItemText>
                        </MenuItem>
                    </Menu>
                </>
            ) : sablony?.length ? (
                <Paper>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Název</TableCell>
                                    <TableCell>Typ události</TableCell>
                                    <TableCell>Lokace</TableCell>
                                    <TableCell align="center">Pořadí</TableCell>
                                    <TableCell>Stav</TableCell>
                                    <TableCell>Akce</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[...sablony].sort((a, b) => a.poradi - b.poradi).map((s) => (
                                    <TableRow key={s.id} hover>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                {s.barva && (
                                                    <Box
                                                        sx={{
                                                            width: 12,
                                                            height: 12,
                                                            borderRadius: '50%',
                                                            bgcolor: s.barva,
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                )}
                                                <Typography variant="body2">{s.nazev}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{getTypLabel(s)}</TableCell>
                                        <TableCell>{s.obec || '-'}</TableCell>
                                        <TableCell align="center">{s.poradi}</TableCell>
                                        <TableCell>
                                            <Box display="flex" gap={0.5} alignItems="center">
                                                <Chip
                                                    label={s.aktivni ? 'Aktivní' : 'Neaktivní'}
                                                    color={s.aktivni ? 'success' : 'default'}
                                                    size="small"
                                                />
                                                {s.predpoplach && (
                                                    <Chip label="PP" color="warning" size="small" variant="outlined" />
                                                )}
                                                {s.userVyhlaseni && (
                                                    <Chip label="U" color="info" size="small" variant="outlined" />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                <Tooltip title="Upravit">
                                                    <IconButton size="small" onClick={() => handleOpenEdit(s)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={s.aktivni ? 'Deaktivovat' : 'Aktivovat'}>
                                                    <IconButton
                                                        size="small"
                                                        color={s.aktivni ? 'default' : 'success'}
                                                        onClick={() => handleToggleStatus(s)}
                                                        disabled={toggleStatusMutation.isPending}
                                                    >
                                                        {s.aktivni
                                                            ? <VisibilityOffIcon fontSize="small" />
                                                            : <CheckCircleIcon fontSize="small" />}
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Smazat">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => setDeleteConfirmId(s.id)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            ) : (
                <EmptyState
                    icon={<DescriptionIcon />}
                    title="Žádné šablony"
                    description="Vytvořte první šablonu pro rychlé vyhlášení poplachu."
                />
            )}

            {/* Wizard Dialog */}
            <SablonaWizardDialog
                open={dialogOpen}
                onClose={() => { setDialogOpen(false); setEditingSablona(null); }}
                editingSablona={editingSablona}
                nextPoradi={nextPoradi}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)}>
                <DialogTitle>Smazat šablonu?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Opravdu chcete smazat tuto šablonu? Šablona bude deaktivována.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmId(null)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                        disabled={deleteMutation.isPending}
                    >
                        Smazat
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SablonyUdalostiPage;
